import axios from 'axios';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND}/api`;

// --- Legacy localStorage token cleanup --------------------------------
// Phase 1 auth hardening dropped localStorage token auth entirely in
// favor of an httpOnly session cookie. Clear any lingering legacy entry
// (silently - private-mode / disabled storage is fine, we just move on).
try {
    localStorage.removeItem('centurion_token');
    localStorage.removeItem('token');
} catch { /* ignore */ }

// --- CSRF ----------------------------------------------------------------
// The CSRF nonce is returned by POST /api/auth/login (and /api/auth/me)
// and kept in-memory for the lifetime of the page. We DELIBERATELY do
// not persist it to localStorage - the whole point is that a malicious
// script on another origin cannot read it. On full page reload the
// AuthProvider rehydrates it via GET /api/auth/session.
let currentCsrfToken = null;
export const getCsrfToken = () => currentCsrfToken;
export const setCsrfToken = (t) => { currentCsrfToken = t || null; };

export const apiClient = axios.create({
    baseURL: API,
    timeout: 15000,
    headers: { 'Content-Type': 'application/json' },
    withCredentials: true, // send + receive the session cookie
});

const SAFE_METHODS = new Set(['get', 'head', 'options']);

apiClient.interceptors.request.use((config) => {
    config.headers = config.headers || {};
    const method = (config.method || 'get').toLowerCase();
    if (!SAFE_METHODS.has(method) && currentCsrfToken) {
        config.headers['X-CSRF-Token'] = currentCsrfToken;
    }
    return config;
});

apiClient.interceptors.response.use(
    (resp) => resp,
    (err) => {
        if (err?.response?.status === 401) {
            const url = err?.config?.url || '';
            if (!url.endsWith('/auth/login') && !url.endsWith('/auth/session')) {
                setCsrfToken(null);
                if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
                    const next = encodeURIComponent(
                        window.location.pathname + window.location.search
                    );
                    window.location.replace(`/login?session=expired&next=${next}`);
                }
            }
        }
        return Promise.reject(err);
    }
);

// Public API helpers (Phase 1)
export const submitQuote = (payload) => apiClient.post('/quotes', payload);
export const submitContact = (payload) => apiClient.post('/contact', payload);

// Auth (Phase 1 hardening)
export const apiLogin = (email, password) =>
    apiClient.post('/auth/login', { email, password }).then((r) => r.data);
export const apiMe = () => apiClient.get('/auth/me').then((r) => r.data);
export const apiSession = () => apiClient.get('/auth/session').then((r) => r.data);
export const apiLogout = () => apiClient.post('/auth/logout').then((r) => r.data);
export const apiChangePassword = (current_password, new_password) =>
    apiClient.post('/auth/change-password', { current_password, new_password }).then((r) => r.data);

// Admin
export const apiAdminQuotes = () => apiClient.get('/quotes').then((r) => r.data);
export const apiAdminQuotePatch = (id, body) =>
    apiClient.patch(`/quotes/${id}`, body).then((r) => r.data);
export const apiAdminContacts = () => apiClient.get('/contacts').then((r) => r.data);
export const apiAdminClients = () =>
    apiClient.get('/admin/clients').then((r) => r.data);
export const apiAdminCreateClient = (body) =>
    apiClient.post('/admin/clients', body).then((r) => r.data);
export const apiAdminUpdateClient = (id, body) =>
    apiClient.patch(`/admin/clients/${id}`, body).then((r) => r.data);
export const apiAdminLeases = () =>
    apiClient.get('/admin/leases').then((r) => r.data);
export const apiAdminCreateLease = (body) =>
    apiClient.post('/admin/leases', body).then((r) => r.data);
export const apiAdminUpdateLease = (id, body) =>
    apiClient.patch(`/admin/leases/${id}`, body).then((r) => r.data);
export const apiAdminDeleteLease = (id) =>
    apiClient.delete(`/admin/leases/${id}`).then((r) => r.data);

// Phase 1: staff user management
export const apiAdminUsers = () =>
    apiClient.get('/admin/users').then((r) => r.data);
export const apiAdminCreateUser = (body) =>
    apiClient.post('/admin/users', body).then((r) => r.data);
export const apiAdminUpdateUser = (id, body) =>
    apiClient.patch(`/admin/users/${id}`, body).then((r) => r.data);
export const apiAdminResetUserPassword = (id) =>
    apiClient.post(`/admin/users/${id}/reset-password`).then((r) => r.data);
export const apiAdminDeleteUser = (id) =>
    apiClient.delete(`/admin/users/${id}`).then((r) => r.data);
export const apiAdminAuditLog = (params) =>
    apiClient.get('/admin/audit-log', { params }).then((r) => r.data);

// Phase 2: CMS content endpoints
export const apiGetPublicContent = (pageSlug) =>
    apiClient.get(`/content/${pageSlug}`).then((r) => r.data);
export const apiAdminContentSchema = () =>
    apiClient.get('/admin/content/schema').then((r) => r.data);
export const apiAdminGetPage = (pageSlug) =>
    apiClient.get(`/admin/content/${pageSlug}`).then((r) => r.data);
export const apiAdminGetSection = (pageSlug, sectionSlug) =>
    apiClient.get(`/admin/content/${pageSlug}/${sectionSlug}`).then((r) => r.data);
export const apiAdminPutSection = (pageSlug, sectionSlug, data) =>
    apiClient.put(`/admin/content/${pageSlug}/${sectionSlug}`, data).then((r) => r.data);

// --- Phase 4: PDF export + pricing -------------------------------------

/**
 * POST the calculator payload and receive a PDF blob. Triggers a download
 * via URL.createObjectURL. Returns { quoteNumber, filename }.
 */
export const exportQuotePdf = async (payload) => {
    const res = await apiClient.post('/exports/quote', payload, {
        responseType: 'blob',
    });
    const quoteNumber = res.headers['x-quote-number'] || '';
    const disp = res.headers['content-disposition'] || '';
    const m = /filename="([^"]+)"/.exec(disp);
    const filename = m ? m[1] : `Centurion_${payload.mode}_${quoteNumber || 'export'}.pdf`;
    const url = URL.createObjectURL(res.data);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
    return { quoteNumber, filename };
};

export const apiGetPricingPublic = () =>
    apiClient.get('/pricing-settings').then((r) => r.data);
export const apiAdminGetPricing = () =>
    apiClient.get('/admin/pricing-settings').then((r) => r.data);
export const apiAdminPatchPricing = (body) =>
    apiClient.patch('/admin/pricing-settings', body).then((r) => r.data);
export const apiAdminExportLogs = () =>
    apiClient.get('/admin/export-logs').then((r) => r.data);

// --- Admin: file uploads (hero slide images) -------------------------
// Upload a single image file. `onProgress` (optional) receives a 0-100
// integer. Returns { ok, url, filename, size, mime, original_filename }.
export const apiAdminUploadImage = (file, onProgress) => {
    const fd = new FormData();
    fd.append('file', file);
    return apiClient
        .post('/admin/uploads', fd, {
            headers: { 'Content-Type': 'multipart/form-data' },
            timeout: 60000,
            onUploadProgress: (evt) => {
                if (!onProgress || !evt.total) return;
                onProgress(Math.round((evt.loaded * 100) / evt.total));
            },
        })
        .then((r) => r.data);
};

export const apiAdminListUploads = () =>
    apiClient.get('/admin/uploads').then((r) => r.data);

export const apiAdminDeleteUpload = (filename) =>
    apiClient.delete(`/admin/uploads/${encodeURIComponent(filename)}`).then((r) => r.data);
